import {UrlService} from '@nuskin/ns-util';

/**
 * This object represents a product document stored in Solr. It is designed to provide ease of access to all of the
 * document properties.
 *
 * @param solrProduct - This is the product document in its raw form as it is returned from an http call.
 * @param [searchScore] - This is the search score for the product when it comes from a search
 *
 * @constructor
 */
export const SolrProductAdaptor = function(solrProduct, searchScore){
    let score = 0;
    if (typeof(searchScore) === "number") {
        score = searchScore;
    }
    let languageAndMarketDocs = solrProduct._childDocuments_;

    let languageDocs = {};
    let marketDocs = {};

    // Object to represent the language document for a Solr product document.
    let SolrProductLanguageAdaptor = function(language){
        let productLanguage = language;

        this.getIndex = function() {
            return productLanguage.path_exact;
        };

        this.getSku = function() {
            return productLanguage.sku && productLanguage.sku.length > 0 ? productLanguage.sku[0] : undefined;
        };

        this.getBaseSku = function() {
            return productLanguage.baseSku && productLanguage.baseSku.length > 0 ? productLanguage.baseSku[0] : undefined;
        };

        this.getMarkets = function() {
            return productLanguage.markets;
        };

        this.getVariants = function() {
            return productLanguage.variants;
        };

        this.getTitle = function() {
            return productLanguage.name && productLanguage.name.length > 0 ? productLanguage.name[0] : undefined;
        };

        this.getDescription = function() {
            return productLanguage.shortDescription && productLanguage.shortDescription.length > 0 ? productLanguage.shortDescription[0] : undefined;
        };

        this.getLongDescription = function() {
            return productLanguage.longDescription && productLanguage.longDescription.length > 0 ? productLanguage.longDescription[0] : undefined;
        }

        this.getIngredients = function() {
            return productLanguage.ingredients;
        };

        this.getCategories = function() {
            return productLanguage.categories;
        };

        this.getSearchTerms = function() {
            return productLanguage.searchTerms;
        };

        this.isBaseProduct = function(){
            return productLanguage.variants;
        };

        this.getIsExclusive = function(){
            return productLanguage.isExclusive && productLanguage.isExclusive.length > 0 ? productLanguage.isExclusive[0] : undefined;
        }

        this.isVariantProduct = function(){
            return productLanguage.baseSku && productLanguage.baseSku.length > 0 ? productLanguage.baseSku[0] : undefined;
        };

        this.getSize = function() {
            return productLanguage.size;
        };

        this.getSalesLabel = function() {
            return productLanguage.salesLabel ? productLanguage.salesLabel : "";
        }

        this.getVariantType = function() {
            return productLanguage.variantType ? productLanguage.variantType : "";
        }

        this.getVariantShade = function() {
            return productLanguage.shade ? productLanguage.shade : "";
        }

        this.getVariantsLabel = function() {
            return productLanguage.variantsLabel ? productLanguage.variantsLabel : "";
        }

        this.getVariantDropdownPlaceholder = function() {
            return productLanguage.variantDropdownPlaceholder ? productLanguage.variantDropdownPlaceholder : "";
        }

        this.getVariantDropdownLabel = function() {
            return productLanguage.variantDropdownLabel ? productLanguage.variantDropdownLabel : "";
        }
    };

    // Object to represent the market document for a Solr product document.
    let SolrProductMarketAdaptor = function(market){
        let productMarket = market;
        let imageUrlPrefix = UrlService.getSiteUrl();

        this.getIndex = function() {
            return productMarket.path_exact;
        };

        this.getSku = function() {
            return productMarket.sku && productMarket.sku.length > 0 ? productMarket.sku[0] : undefined;
        };

        this.getPurchasable = function() {
            if (productMarket.statusCode && productMarket.statusCode.length > 0 && productMarket.webEnabled && productMarket.webEnabled.length > 0) {
                if (productMarket.statusCode[0] == 10 && productMarket.webEnabled[0] == 'true')
                    return true;
            }
            return false;
        };

        //This returns a URL that points to the fullImage in the PMD.  You can set the resolution by appending a width and extension
        // EX: getImageUrl() + ".90.png";  would return the image with a width of 90px in the png format
        this.getImageUrl = function() {
            let sku = productMarket.sku && productMarket.sku.length > 0 ? productMarket.sku[0] : undefined;
            let skuURL = getSkuURL(sku);
            return skuURL ? skuURL + "/jcr:content/fullImage.img.png" : 'noImage.jpg';
        };

        this.getFullImageUrl = function() {
            if (productMarket.fullImage && productMarket.fullImage.length > 0) {
                return imageUrlPrefix + productMarket.fullImage[0];
            }
            return 'noImage.jpg';
        };

        this.getRetailPrice = function(){
            return productMarket.priceData;
        };

        this.getTotalAvailableQuantity = function(){
            return productMarket.totalAvailableQuantity;
        };

        this.setRetailPrice = function(priceData){
            productMarket.priceData = priceData;
        };

        this.setTotalAvailableQuantity = function(totalAvailableQuantity){
            productMarket.totalAvailableQuantity = totalAvailableQuantity;
        };

        this.getOrderType = function() {
            return productMarket.orderType;
        };

        this.getCustType = function() {
            return productMarket.custType;
        };

        this.getScanQualified = function() {
            return productMarket.scanQualified;
        };

        this.getStatusCode = function() {
            return productMarket.statusCode;
        };

        this.getStatusMessage = function() {
            return productMarket.statusMessage;
        };

        this.getWebEnabled = function() {
            return productMarket.webEnabled;
        }

        function getSkuURL(sku) {
            if (sku) {
                if (sku.length != 8) return undefined;
                let skuURL = imageUrlPrefix + "/content/products/";
                skuURL += sku.substring(0,2) + "/";
                skuURL += sku.substring(2,4) + "/";
                skuURL += sku.substring(4,6) + "/";
                skuURL += sku;
                return skuURL;
            } else {
                return undefined;
            }
        }
    };


    this.getIndex = function() {
        return solrProduct.path_exact;
    };

    this.getSku = function() {
        return solrProduct.sku && solrProduct.sku.length > 0 ? solrProduct.sku[0] : undefined;
    };

    this.getBaseSku = function() {
        return solrProduct.baseSku && solrProduct.baseSku.length > 0 ? solrProduct.baseSku[0] : undefined;
    };

    this.getMarkets = function() {
        return solrProduct.markets;
    };

    this.getVariants = function() {
        return solrProduct.variants;
    };

    this.getLanguages = function() {
        return solrProduct.languages;
    };

    this.getRoot = function() {
        return solrProduct._root_ && solrProduct._root_.length > 0 ? solrProduct._root_[0] : undefined;
    };

    this.getVersion = function() {
        return solrProduct._version_;
    };

    this.getSearchScore = function() {
        return score;
    };

    /**
     *
     * @param lang - Example: "en"
     * @returns the language document in the product for the given language.
     */
    this.getLanguageChild = function(lang){
        let languageChild = null;

        if (languageAndMarketDocs) {
            for (let i = 0; i < languageAndMarketDocs.length; i++) {
                if (languageAndMarketDocs[i].path_exact === "product/" + this.getSku() + '/' + lang.toLowerCase()) {
                    languageChild = languageDocs[lang];

                    if (!languageChild){
                        languageChild = new SolrProductLanguageAdaptor(languageAndMarketDocs[i]);
                        languageDocs[lang] = languageChild;
                    }

                    break;
                }
            }
        }

        return languageChild;
    };

    /**
     *
     * @param market - Example: "US"
     * @returns the market document in the product for the given market.
     */
    this.getMarketChild = function(market){
        let marketChild = null;

        if (languageAndMarketDocs) {
            for (let i = 0; i < languageAndMarketDocs.length; i++) {
                if (languageAndMarketDocs[i].path_exact === "product/" + this.getSku() + '/' + market.toUpperCase()) {
                    marketChild = marketDocs[market];

                    if (!marketChild){
                        marketChild = new SolrProductMarketAdaptor(languageAndMarketDocs[i]);
                        marketDocs[market] = marketChild;
                    }

                    break;
                }
            }
        }

        return marketChild;
    };

    this.isBaseProduct = function(){
        return solrProduct.variants;
    };

    this.isVariantProduct = function(){
        return solrProduct.baseSku && solrProduct.baseSku.length > 0 ? solrProduct.baseSku[0] : undefined;
    };
};
